import axios from "axios";

import { initDeviceId } from "@utils/util";
import { LOCALSTORAGE_KEY } from "@config";
import { resolveTraceIdFromHeaders } from "./http/traceId";

/**
 * 给抛出的 axios 错误对象补齐 status/code/message/traceID/traceId，
 * 方便上层 toast 与埋点统一获取 trace id。
 */
const enrichAxiosUploadError = (error: unknown): unknown => {
  if (!axios.isAxiosError(error)) return error;
  const responseData = error.response?.data as { code?: string | number; message?: string } | undefined;
  const traceId = resolveTraceIdFromHeaders(error.response?.headers as Record<string, string> | undefined);

  Object.assign(error as unknown as Record<string, unknown>, {
    status: error.response?.status,
    traceID: traceId,
    traceId,
  });
  // 业务返回 code 时覆盖 axios 默认 code（例如 ERR_BAD_REQUEST），便于上层做错误码分支
  if (responseData?.code !== undefined && responseData?.code !== null && responseData.code !== "") {
    (error as unknown as { code?: string }).code = String(responseData.code);
  }
  if (responseData?.message) {
    (error as unknown as { message?: string }).message = responseData.message;
  }
  return error;
};

/**
 * 上传海报图像
 * @param reqData - 要上传的Base64数据
 * @returns 返回泛型T的Promise，表示上传操作的响应
 */
const uploadPosterImage = async <T>(reqData: FormData) => {
  try {
    await initDeviceId();

    const response = await axios.post<T>(`/poster/upload`, reqData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Upload failed:", error);
    throw enrichAxiosUploadError(error);
  }
};

/**
 * 上传用户头像
 * @param avatarFile - 要上传的头像文件
 * @returns 返回Promise，包含上传后的头像URL
 */
const uploadAvatar = async (avatarFile: File): Promise<string> => {
  try {
    await initDeviceId();

    const accessToken = localStorage.getItem(LOCALSTORAGE_KEY.ACCESS_TOKEN);
    const formData = new FormData();
    formData.append("file", avatarFile);

    const response = await axios.post<{ data: string }>(`/api/users/modify-avatar`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: accessToken ? `Bearer ${accessToken}` : "",
      },
    });

    return response.data.data;
  } catch (error) {
    console.error("Avatar upload failed:", error);
    throw enrichAxiosUploadError(error);
  }
};

export interface ProductImageResult {
  assetId: number;
  imageUrl: string;
  success: boolean;
  compliant?: boolean;
  rejectionReason?: string;
  description?: string;
}

/**
 * 视频生成上传商品图片（支持单张或多张）
 * @param files - 单个文件或文件数组
 * @returns 单张时返回单个结果，多张时返回结果数组
 */
async function uploadProductImage(files: File | File[]): Promise<ProductImageResult[]> {
  try {
    await initDeviceId();

    const formData = new FormData();
    const fileList = Array.isArray(files) ? files : [files];
    fileList.forEach((file) => formData.append("images", file));

    const accessToken = localStorage.getItem(LOCALSTORAGE_KEY.ACCESS_TOKEN);

    const response = await axios.post(`/api/video/upload-images`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: accessToken ? `Bearer ${accessToken}` : "",
      },
    });
    return response.data.data;
  } catch (error) {
    console.error("Product image upload failed:", error);
    throw enrichAxiosUploadError(error);
  }
}

export { uploadAvatar, uploadPosterImage, uploadProductImage };
